import type { ProductionHistoryWindow } from "@/features/devices/types";
import { formatTag, parseTag } from "@/features/machines/addressing";
import type { GhmSignalKind } from "@ghm/mqtt-contract";

export type ProductionMode = "rate" | "hourly" | "total";
export type ProductionTotalMode = "physical" | "baseline" | "lot";

/** Total nativo em v nao e ritmo nem o totalizador acc usado pelo KPI. */
export function isNativeCumulativeSource(source: string, signalKind: GhmSignalKind | undefined): boolean {
  return signalKind === "cumulative" && parseTag(source)?.signal === "v";
}

interface ProductionTotalSettings {
  mode: ProductionMode;
  window?: "current" | ProductionHistoryWindow;
  target?: number;
  totalMode?: ProductionTotalMode;
}

/**
 * Widgets antigos representavam lote apenas pela presenca de `target`.
 * Centralizar a leitura mantem essa compatibilidade e permite que a baseline
 * sem meta seja persistida explicitamente como `baseline`.
 */
export function resolveProductionTotalMode(
  production: ProductionTotalSettings | undefined,
): ProductionTotalMode {
  if (production?.mode !== "total" || (production.window && production.window !== "current")) {
    return "physical";
  }
  if (
    production.totalMode === "physical" ||
    production.totalMode === "baseline" ||
    production.totalMode === "lot"
  ) {
    return production.totalMode;
  }
  return typeof production.target === "number" && production.target > 0 ? "lot" : "physical";
}

/**
 * KPIs instantaneos usam o valor calculado pelo firmware (`v`). O total
 * acumulado usa o totalizador persistente (`acc`) da mesma entrada.
 */
export function resolveProductionValueSource(
  source: string,
  mode: ProductionMode,
): string {
  const address = parseTag(source);
  if (!address) return source;

  return formatTag({
    ...address,
    signal: mode === "total" ? "acc" : "v",
  });
}

export function productionModesForSignal(
  signal: "s" | "acc" | "v" | undefined,
  signalKind?: GhmSignalKind,
): ProductionMode[] {
  if (signal === "acc") return ["total"];
  if (signal === "v" && signalKind === "cumulative") return [];
  if (signal === "v") return ["rate", "hourly"];
  return [];
}

export function isProductionModeCompatible(
  signal: "s" | "acc" | "v" | undefined,
  mode: ProductionMode,
  signalKind?: GhmSignalKind,
): boolean {
  return productionModesForSignal(signal, signalKind).includes(mode);
}
